/**
 * The Settings screen — the gear in the top-right chrome.
 *
 * Five sections plus an admin link:
 *   Profile   the survivor, the account, the password, preferences, the
 *             billing address, and the delete-account danger zone
 *   Sound     master / effects / music, level + mute each
 *   Tutorial  one card: replay the eight-step walkthrough
 *   Invoices  every payment receipted, with the method core's own list omits
 *   Ledger    every Cash and Gold movement, purchases named
 *   Legal     the three documents, read in place
 *   Admin     a link to the built-in admin panel, for ADMIN accounts only
 *
 * ─── Why this is not a `ui/panels.ts` panel ─────────────────────────────────
 * Everything in `base-view/ui/` belongs to the settlement view and is torn
 * down with it. This screen opens from the chrome toolbar, which OUTLIVES that
 * view — a Location visit unmounts base-view entirely and rebuilds it — so a
 * settings screen registered as a game panel would vanish mid-visit, taking
 * its registration with it and leaving `worldInputBlocked()` answering for a
 * dead element. It lives beside `bootstrap.ts` instead, with its own stylesheet
 * and its own Escape handling, and it does not register.
 *
 * Nothing is lost by that: the screen covers the whole viewport at z-index
 * 1250, above every game panel, so there is no world input left to block.
 *
 * What WAS lost, until 2026-09-18, is the keyboard. base-view listens on
 * `window` and this screen on `document`, so its hotkeys still fired under an
 * open Settings screen and opened game windows nobody could see. The screen now
 * stops keydown propagation while it is open (see `onKeyDown` below) — the same
 * outcome a registration would have bought, stated by the file that owns the
 * rule and without coupling the settlement view to the chrome that outlives it.
 *
 * ─── Rendering ──────────────────────────────────────────────────────────────
 * The body is rewritten on every store push, which is the pattern `home-ui.ts`
 * and `quest-ui.ts` already use, with ONE exception that this screen forces:
 * form inputs. Rewriting the DOM under a half-typed street address throws the
 * typing away and moves the caret. So the forms are rendered once per tab
 * ENTRY and read on submit — `renderedTab`/`renderedProfileId` below are what
 * make the difference between "the store changed" and "the player is looking
 * at something else now".
 */
import { API_BASE } from '../../core/network/api';
import { getPaymentMethods, getWithdrawalLimits } from '../../core/payments';
import type { PayoutMethod, WithdrawalLimits } from '../../core/payments';
import { mountGoldWithdrawForm } from './withdraw-gold-form';
import type { GoldWithdrawFormHandle } from './withdraw-gold-form';
import { getSiteInfo } from '../../core/preview/preview';
import { authStore } from '../../core/state/auth-store';
import { CharacterPreview } from '../base-view/ui/character-preview';
import { gameStateStore } from '../state/game-state';
import { legalPath } from '../landing/legal-paths';
import {
  accountStateStore, canOpenAdmin, changePassword, clearAccountMessages, CORE_TO_PROFILE_FIELD,
  deleteAccount, LEGAL_DOCUMENTS, loadAccount, loadInvoices, loadLedger,
  resetAccountState, saveProfile, savePreferences, setBillingErrors, setSettingsTab,
} from '../state/account-state';
import { PROFILE_FIELDS } from '../state/account-state.types';
import type {
  AccountInvoice, AccountLedgerEntry, AccountProfile, AccountState, ProfileField, SettingsTab,
} from '../state/account-state.types';
import {
  BILLING_LIMITS, fieldError, normalise, STATE_REQUIRED_COUNTRIES,
} from '../../core/payments/billing-address';
import type { BillingField } from '../../core/payments/billing-address';
import {
  AUDIO_CHANNELS, audioSettingsStore, setChannelLevel, toggleChannelMuted,
} from '../state/audio-settings';
import type { AudioChannelId, AudioSettings } from '../state/audio-settings';
import { controlSettingsStore, toggleFireWithSpace } from '../state/control-settings';
import type { ControlSettings } from '../state/control-settings';
import { hasSeenWalkthrough, requestWalkthrough } from '../state/tutorial-state';
import { goldIconHtml } from '../base-view/ui/hud';
import { cashCoinHtml } from '../base-view/ui/coin';
import { requestTutorialYard } from '../state/tutorial-yard-state';
import { countries } from './countries';
import SETTINGS_CSS from './settings.css';
import CLOSE_CSS from '../base-view/ui/styles/close.css';
import { createCloseButton } from '../base-view/ui/close-button';

const ROOT_ID = 'tg-settings';
const STYLE_ID = 'tg-settings-styles';

export const ADMIN_PATH = '/admin-panel';

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'PROFILE', label: 'Profile' },
  { id: 'SOUND', label: 'Sound' },
  { id: 'CONTROLS', label: 'Controls' },
  { id: 'TUTORIAL', label: 'Tutorial' },
  // The id stays BILLING — it is internal, and `SettingsTab` is shared with
  // `account-state`. Only the label the player reads changed.
  { id: 'BILLING', label: 'Invoices' },
  { id: 'LEDGER', label: 'Ledger' },
  { id: 'LEGAL', label: 'Legal' },
];

let unsubscribers: Array<() => void> = [];
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let preview: CharacterPreview | null = null;
let previewHost: HTMLElement | null = null;
/**
 * Teardown for the survivor card's pointer listeners.
 *
 * Module-level and beside `previewHost` for the same reason that is: the card
 * is REBUILT on every structural render (see `render`), so an attach with no
 * matching detach leaks one listener set per tab entry. `mountProfileCard`
 * calls this before it attaches, and `disposeSettingsUI` calls it last.
 */
let unbindProfileCard: (() => void) | null = null;
/**
 * What the last full render drew, so a store push that changes neither does
 * not rewrite the DOM under a half-typed form. See the header.
 */
let renderedTab: SettingsTab | null = null;
let renderedProfileId: string | null = null;
/**
 * The showing tab's own fetched list AS IT WAS at the last full paint, held by
 * reference — see `render`. `unknown` because the two tabs it covers hold
 * different shapes and nothing here reads into it; only identity matters.
 */
let renderedData: unknown = null;
/** Set by the caller; the screen never reaches into the auth module itself. */
let signOut: (() => void) | null = null;
/**
 * The "are you sure" for the delete button, injected rather than imported.
 *
 * `bootstrap.ts` owns `confirmDialog` and also owns this screen, so importing
 * it here would be a cycle — one that ESM tolerates today only because both
 * uses are deferred to call time, which is a property nobody would think to
 * preserve. Injecting it costs one line at the call site and buys two things:
 * no cycle, and a test that can assert the delete goes THROUGH a confirmation
 * rather than straight to the server. That second one is the point — the same
 * regression `createChrome`'s docstring describes for the sign-out button.
 */
let confirmAction: ConfirmFn | null = null;
let withdrawHandle: GoldWithdrawFormHandle | null = null;
let cachedPayoutMethods: PayoutMethod[] | null = null;
let cachedWithdrawLimits: WithdrawalLimits | null = null;

/** Matches `bootstrap.ts`'s `confirmDialog`; returns its teardown. */
export type ConfirmFn = (opts: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}) => () => void;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `${CLOSE_CSS}\n${SETTINGS_CSS}`;
  document.head.appendChild(style);
}

/**
 * Builds the screen, hidden, and returns it for the caller to append.
 *
 * `onSignOut` is how a password change or an account deletion ends the
 * session. Both revoke every refresh token server-side, so staying on the
 * screen afterwards would leave the player looking at a panel backed by a dead
 * session until something happened to notice.
 */
export function createSettingsUI(opts: {
  onSignOut: () => void;
  /** `bootstrap.ts`'s `confirmDialog`. See `confirmAction` for why it is passed in. */
  confirm: ConfirmFn;
}): HTMLElement {
  ensureStyles();
  signOut = opts.onSignOut;
  confirmAction = opts.confirm;

  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.className = 'tg-set';
  root.hidden = true;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Settings');

  const panel = document.createElement('div');
  panel.className = 'tg-set-panel';

  const rail = document.createElement('nav');
  rail.className = 'tg-set-rail';

  const close = createCloseButton({ label: 'Close settings', className: 'tg-set-close', onClick: () => closeSettingsUI() });

  const body = document.createElement('div');
  body.className = 'tg-set-body';

  panel.append(rail, close, body);
  root.appendChild(panel);

  root.addEventListener('pointerdown', (e) => {
    if (e.target === root) closeSettingsUI();
  });

  rail.addEventListener('click', (e) => {
    const tab = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-set-tab]');
    if (tab) setSettingsTab(tab.dataset.setTab as SettingsTab);
  });

  body.addEventListener('click', (e) => onBodyClick(e, body));
  // The state/province field only exists for the countries core's gateway
  // wants one for — follow the dropdown without rebuilding the typed form.
  body.addEventListener('change', (e) => {
    const t = e.target as HTMLSelectElement | null;
    if (t?.name === 'billingCountry') syncStateField(body, t.value);
  });

  onKeyDown = (e: KeyboardEvent) => {
    if (!isSettingsUIOpen()) return;
    // Everything else on the page stops hearing the keyboard while this screen
    // is up. Found 2026-09-18, while making the game show one window at a time:
    // base-view's own handler is on `window` and this one is on `document`, so
    // a bubbling keypress reached BOTH — and because this screen deliberately
    // does not register as a game panel (see the header), nothing gated them.
    // Pressing P over an open Settings screen opened the Black Market
    // underneath it, at z-index 1001 below this screen's 1250, where the player
    // found it on closing Settings and had never asked for it.
    //
    // Stopped here rather than by importing a predicate into base-view: that
    // would couple the settlement view to the chrome that outlives it, and the
    // rule is simply "this screen owns the keyboard", which is this file's to
    // state. Typing is unaffected — an input's own handling happens at the
    // target, before this listener ever runs.
    e.stopPropagation();
    if (e.key !== 'Escape') return;
    e.preventDefault();
    closeSettingsUI();
  };
  document.addEventListener('keydown', onKeyDown);

  const repaint = (): void => {
    if (!isSettingsUIOpen()) return;
    render(rail, body, accountStateStore.get());
  };
  unsubscribers = [
    accountStateStore.subscribe(repaint),
    audioSettingsStore.subscribe(repaint),
    // Without this the Controls toggle writes the setting and persists it, and
    // its own label never changes — the tab is repainted from `repaint`, and
    // nothing else pushes when a control setting does.
    controlSettingsStore.subscribe(repaint),
    gameStateStore.subscribe(repaint),
  ];

  preview = new CharacterPreview();
  previewHost = document.createElement('div');
  previewHost.className = 'tg-set-portrait';
  preview.mount(previewHost);

  return root;
}

export function openSettingsUI(): void {
  const el = document.getElementById(ROOT_ID);
  if (!el) return;
  el.hidden = false;
  // Forces a full paint: the tab did not change, but the screen was closed and
  // its DOM is whatever the last session left behind.
  renderedTab = null;
  renderedProfileId = null;
  renderedData = null;
  clearAccountMessages();
  void loadAccount();
  const rail = el.querySelector<HTMLElement>('.tg-set-rail');
  const body = el.querySelector<HTMLElement>('.tg-set-body');
  if (rail && body) render(rail, body, accountStateStore.get());
}

/**
 * Opens Settings on the Profile tab, scrolled to the billing address — where
 * the Gold shop sends a player whose payment method needs a full address.
 * The form renders once the profile has loaded, so this waits for it (a few
 * frames, never forever).
 */
export function openSettingsAtBilling(): void {
  setSettingsTab('PROFILE');
  openSettingsUI();
  let frames = 0;
  const seek = (): void => {
    const target = document.getElementById(ROOT_ID)?.querySelector<HTMLElement>('#billing-address');
    if (target) {
      target.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      target.querySelector<HTMLElement>('input')?.focus({ preventScroll: true });
      return;
    }
    if (++frames < 120) requestAnimationFrame(seek);
  };
  requestAnimationFrame(seek);
}

export function isSettingsUIOpen(): boolean {
  const el = document.getElementById(ROOT_ID);
  return !!el && !el.hidden;
}

export function closeSettingsUI(): void {
  const el = document.getElementById(ROOT_ID);
  if (el) el.hidden = true;
  // Deactivated first and always: the preview holds a WebGL context and a
  // ticking animation mixer, and leaving it running behind a hidden panel
  // burns a frame budget nobody is looking at.
  preview?.setActive(false);
  renderedTab = null;
  renderedProfileId = null;
  renderedData = null;
  resetAccountState();
}

export function disposeSettingsUI(): void {
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  unbindProfileCard?.();
  unbindProfileCard = null;
  withdrawHandle?.destroy();
  withdrawHandle = null;
  cachedPayoutMethods = null;
  cachedWithdrawLimits = null;
  preview?.dispose();
  preview = null;
  previewHost = null;
  renderedTab = null;
  renderedProfileId = null;
  renderedData = null;
  signOut = null;
  confirmAction = null;
  document.getElementById(ROOT_ID)?.remove();
}

// ─── Routing ────────────────────────────────────────────────────────────────

function onBodyClick(e: Event, body: HTMLElement): void {
  const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-set-action]');
  if (!target) return;
  const action = target.dataset.setAction!;
  const value = target.dataset.value ?? '';

  switch (action) {
    case 'save-profile': void submitProfile(body); break;
    case 'save-prefs': void submitPreferences(body); break;
    case 'change-password': void submitPassword(body); break;
    case 'delete-account': askDelete(body); break;
    case 'mute': toggleChannelMuted(value as AudioChannelId); break;
    case 'fire-space': toggleFireWithSpace(); break;
    case 'sign-out-now': signOut?.(); break;
    case 'start-tutorial': beginTutorial(); break;
    case 'start-yard': closeSettingsUI(); requestTutorialYard(); break;
    default: break;
  }
}

function fieldValue(body: HTMLElement, name: string): string {
  return body.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)?.value ?? '';
}

/** `PATCH /account` key → core billing field, for the inline rules. */
const PROFILE_TO_CORE_FIELD = Object.fromEntries(
  Object.entries(CORE_TO_PROFILE_FIELD).map(([core, form]) => [form, core]),
) as Partial<Record<ProfileField, BillingField>>;

/**
 * Sends only what CHANGED.
 *
 * `PATCH /account` leaves an omitted key alone and clears one sent as `""`, so
 * posting every field every time would rewrite columns the player never
 * touched — harmless while the form is in step with the server, and a silent
 * data loss the moment it is not.
 *
 * The changed address fields are checked first with core's own rules
 * (`core/payments/billing-address.ts`, the copy of what the server and the
 * payment gateway enforce), so a wrong post code is named under its input
 * instead of coming back as one line at the top.
 */
async function submitProfile(body: HTMLElement): Promise<void> {
  const current = accountStateStore.get().profile;
  if (!current) return;
  const patch: Record<string, string> = {};
  for (const f of PROFILE_FIELDS) {
    if (f === 'billingState' && !stateRequired(fieldValue(body, 'billingCountry'))) {
      // Hidden for this country: clear a code left over from one that used it,
      // or the invoice would carry it.
      if (current.billingState) patch.billingState = '';
      continue;
    }
    const next = fieldValue(body, f).trim();
    if (next !== (current[f] ?? '')) patch[f] = next;
  }
  if (Object.keys(patch).length === 0) {
    accountStateStore.set((s) => ({ ...s, notice: 'Nothing to save.', error: null }));
    return;
  }
  const errors = billingErrorsFor(current, patch);
  if (Object.keys(errors).length > 0) {
    setBillingErrors(errors);
    return;
  }
  await saveProfile(patch);
}

/** Core's rule for each touched field, against the merged address; a country change re-checks the state. */
function billingErrorsFor(current: AccountProfile, patch: Record<string, string>): Partial<Record<ProfileField, string>> {
  const value = (f: ProfileField) => normalise(f in patch ? patch[f] : current[f]);
  const country = value('billingCountry')?.toUpperCase() ?? null;
  const touched = new Set(Object.keys(patch));
  if (touched.has('billingCountry')) touched.add('billingState');
  const errors: Partial<Record<ProfileField, string>> = {};
  for (const f of touched as Set<ProfileField>) {
    const core = PROFILE_TO_CORE_FIELD[f];
    if (!core) continue;
    const v = core === 'state' || core === 'country' ? value(f)?.toUpperCase() ?? null : value(f);
    const err = fieldError(core, v, country);
    if (err) errors[f] = err;
  }
  return errors;
}

function stateRequired(country: string | null | undefined): boolean {
  return !!country && STATE_REQUIRED_COUNTRIES.includes(country.toUpperCase());
}

function syncStateField(body: HTMLElement, country: string): void {
  const wrap = body.querySelector<HTMLElement>('[data-set-state-field]');
  if (wrap) wrap.hidden = !stateRequired(country);
}

async function submitPreferences(body: HTMLElement): Promise<void> {
  const current = accountStateStore.get().profile;
  if (!current) return;
  const locale = fieldValue(body, 'locale');
  const currency = fieldValue(body, 'currency');
  await savePreferences({
    ...(locale && locale !== current.locale ? { locale } : {}),
    ...(currency && currency !== current.currency ? { currency } : {}),
  });
}

/**
 * The three password fields.
 *
 * The repeat is checked HERE and not on the server, because the server has no
 * business knowing a field was typed twice — it is a typo guard, not a rule.
 * The 8-character minimum IS the server's policy (`ChangePasswordDto`), and it
 * is checked here too so the player is told before spending one of five
 * attempts per fifteen minutes on a 400.
 */
async function submitPassword(body: HTMLElement): Promise<void> {
  const currentPassword = fieldValue(body, 'currentPassword');
  const newPassword = fieldValue(body, 'newPassword');
  const repeat = fieldValue(body, 'repeatPassword');

  if (!currentPassword || !newPassword) {
    accountStateStore.set((s) => ({ ...s, error: 'Fill in every password field.', notice: null }));
    return;
  }
  if (newPassword !== repeat) {
    accountStateStore.set((s) => ({ ...s, error: 'The two new passwords do not match.', notice: null }));
    return;
  }
  if (newPassword.length < 8) {
    accountStateStore.set((s) => ({ ...s, error: 'A new password needs at least 8 characters.', notice: null }));
    return;
  }
  await changePassword(currentPassword, newPassword);
}

/**
 * The delete-account prompt.
 *
 * Two gates, not one. The password field is the server's requirement; the
 * confirm dialog on top is the "additional confirmation prompt" the brief asks
 * for, and it is `bootstrap.ts`'s `confirmDialog` rather than a second one
 * written here — that helper already focuses CANCEL (so a stray Enter cannot
 * complete the action), closes on Escape and on a backdrop click, and renders
 * at z-index 1300, above this screen.
 */
function askDelete(body: HTMLElement): void {
  const password = fieldValue(body, 'deletePassword');
  if (!password) {
    accountStateStore.set((s) => ({ ...s, error: 'Type your password to confirm.', notice: null }));
    return;
  }
  confirmAction?.({
    title: 'Delete this account',
    body: 'Everything goes: the survivor, the hideout, the house, every item and '
      + 'every quest. This cannot be undone and there is no restore.',
    confirmLabel: 'Delete it',
    onConfirm: () => {
      void deleteAccount(password).then((gone) => {
        if (gone) signOut?.();
      });
    },
  });
}

// ─── Rendering ──────────────────────────────────────────────────────────────

function render(rail: HTMLElement, body: HTMLElement, state: AccountState): void {
  renderRail(rail, state);

  // The forms are only rebuilt when the player has moved somewhere else, or
  // when the profile behind them was replaced by a server response. Without
  // this every keystroke in a sibling store (the game state ticks) would wipe
  // a half-typed address.
  // The key covers BOTH server-backed things the forms are built from. The
  // profile alone would have been enough for the load path (they arrive in one
  // `Promise.all` and therefore in one push), which is exactly why it is not
  // the test to write: a later i18n arrival would leave the language and
  // currency dropdowns showing their one-entry fallback for good, and nothing
  // would look wrong in the code.
  const profileId = state.profile || state.i18n
    ? JSON.stringify({ p: state.profile, i: state.i18n })
    : null;
  /**
   * The Ledger and Invoices tabs fetch on their FIRST render (`ledgerTab` calls
   * `loadLedger()` from inside the template when `state.ledger` is null) and
   * then have nothing to repaint them: the arrival changes neither the tab nor
   * the profile, so `structural` stayed false and the screen sat on "Reading
   * the ledger…" until the player switched tabs and back. Both tabs were
   * permanently stuck for anyone who opened them directly.
   *
   * Scoped to the tab that is actually showing rather than folded into the key
   * for every tab: a ledger arriving while the player is mid-address on Profile
   * must not rebuild that form under them, which is the whole reason this gate
   * exists. On any other tab this is `null` both times, so it never fires.
   *
   * By IDENTITY, not by "has it landed yet": the first version was a boolean
   * that latched, so the FIRST push through repainted and every later one did
   * not. That was invisible while each list was fetched exactly once — and
   * wrong the moment a second fetch answered, which is what happens when a
   * Gold purchase completes with the Ledger open, or when the list arrives
   * empty and the first real row lands after it. `loadLedger`/`loadInvoices`
   * write a fresh object every time, so a re-fetch is a new reference and
   * repaints; an unrelated push carries the same one and does not.
   */
  const tabData = state.tab === 'LEDGER'
    ? state.ledger
    : state.tab === 'BILLING' ? state.invoices : null;
  const structural = state.tab !== renderedTab
    || profileId !== renderedProfileId
    || tabData !== renderedData;

  if (structural) {
    renderedTab = state.tab;
    renderedProfileId = profileId;
    renderedData = tabData;
    body.innerHTML = tabBody(state);
    mountPreview(body, state.tab);
    mountProfileCard(body);
    mountWithdrawals(body, state.tab);
  }
  // Messages, the legal reader, the mixer and the button states repaint on
  // every push — none of them holds typed input, and all four are what the
  // player is waiting on.
  //
  // The DISABLED state used to be part of the structural key, which was a
  // quiet bug: `savePreferences` and `changePassword` both set `busy: true`
  // synchronously, so submitting one section rebuilt the whole tab and threw
  // away a half-typed address in another. Setting the attribute in place keeps
  // both properties — the buttons follow `busy`, the form keeps what was typed.
  for (const b of body.querySelectorAll<HTMLButtonElement>('button[data-set-action]')) {
    b.disabled = state.busy;
  }
  renderMessages(body, state);
  renderBillingErrors(body, state);
  if (state.tab === 'SOUND') renderMixer(body, audioSettingsStore.get());
  if (state.tab === 'CONTROLS') renderControls(body, controlSettingsStore.get());
}

function renderRail(rail: HTMLElement, state: AccountState): void {
  const admin = canOpenAdmin(state.profile)
    ? `<a class="tg-set-admin" href="${ADMIN_PATH}">Admin panel</a>`
    : '';
  rail.innerHTML = `
    <p class="tg-set-brand">Settings</p>
    ${TABS.map((t) => `<button type="button" class="tg-set-tab${state.tab === t.id ? ' tg-set-tab-on' : ''}"
      data-set-tab="${t.id}" aria-current="${state.tab === t.id}">${t.label}</button>`).join('')}
    <div class="tg-set-rail-spacer"></div>
    ${admin}
  `;
}

function renderMessages(body: HTMLElement, state: AccountState): void {
  const slot = body.querySelector<HTMLElement>('.tg-set-msgs');
  if (!slot) return;
  const ended = state.sessionEnded
    ? `<div class="tg-set-msg tg-set-msg-ok">Your session ended. <button type="button"
         class="tg-set-btn" data-set-action="sign-out-now">Back to sign-in</button></div>`
    : '';
  slot.innerHTML = `
    ${state.error ? `<div class="tg-set-msg tg-set-msg-err">${esc(state.error)}</div>` : ''}
    ${state.notice && !state.sessionEnded ? `<div class="tg-set-msg tg-set-msg-ok">${esc(state.notice)}</div>` : ''}
    ${ended}
  `;
}

/**
 * Moves the survivor preview into the Profile tab's slot, and switches it off
 * everywhere else.
 *
 * MOVED, not rebuilt: the host carries the canvas `CharacterPreview.build()`
 * appended on first activation, and that build never runs again — so a fresh
 * host per render would orphan the canvas in a detached tree. Same shape as
 * `home-ui.ts`'s `mountPreview`, and for the same two reasons it documents:
 * the host must be positioned, and `setActive(true)` must come after the box
 * is laid out.
 */
function mountWithdrawals(body: HTMLElement, tab: SettingsTab): void {
  withdrawHandle?.destroy();
  withdrawHandle = null;
  if (tab !== 'BILLING') return;
  const el = body.querySelector<HTMLElement>('[data-withdraw-mount]');
  if (!el) return;
  el.textContent = 'Loading withdrawal options…';

  void (async () => {
    try {
      if (!cachedPayoutMethods) {
        const resp = await getPaymentMethods();
        cachedPayoutMethods = resp.withdrawals.methods.filter((m) => m.enabled);
      }
      if (!cachedWithdrawLimits) {
        cachedWithdrawLimits = await getWithdrawalLimits();
      }
    } catch {
      if (!el.isConnected) return;
      el.textContent = 'Could not load withdrawal options.';
      return;
    }
    if (!el.isConnected) return;
    if (!cachedWithdrawLimits.enabled || cachedPayoutMethods.length === 0) {
      el.textContent = 'Withdrawals are not available right now.';
      return;
    }
    const profile = accountStateStore.get().profile;
    const character = gameStateStore.get().character;
    const goldBalance = character?.state === 'ACTIVE' ? character.premiumBalance : 0;
    el.textContent = '';
    withdrawHandle = mountGoldWithdrawForm(el, {
      methods: cachedPayoutMethods,
      limits: cachedWithdrawLimits,
      goldBalance,
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      onSubmitted() {
        cachedWithdrawLimits = null;
      },
    });
  })();
}

function mountPreview(body: HTMLElement, tab: SettingsTab): void {
  const slot = body.querySelector<HTMLElement>('.tg-set-portrait-slot');
  if (tab !== 'PROFILE' || !slot || !preview || !previewHost) {
    preview?.setActive(false);
    return;
  }
  slot.replaceWith(previewHost);
  preview.setActive(true);
}

/**
 * Wires the survivor card's hover lift and cursor-follow tilt.
 *
 * Janeks: *"when i hover on the player card, card itself lifts up. Use depth
 * and animate based on cursor position in the card."*
 *
 * ─── Through CSS custom properties, never through the store ─────────────────
 * The handler writes `--tilt-x`, `--tilt-y` and the glare's `--gx`/`--gy`
 * straight onto the card element. A store push would be the obvious
 * alternative and would be a bug: this screen rebuilds the tab body whenever
 * the structural key changes, so a pointermove-driven push would rewrite the
 * DOM under a half-typed billing address sixty times a second. Writing a
 * custom property mutates nothing this module renders from.
 *
 * ─── And it is REPLACED, not added ──────────────────────────────────────────
 * The card is part of `tabBody`'s string, so every structural render is a new
 * element and this runs again. Without the detach at the top, entering Profile
 * three times would leave three handler sets on three cards — two of them on
 * detached nodes, which is a leak rather than a visible fault, and therefore
 * exactly the kind that survives review. `settings-ui.test.ts` asserts the
 * count after a re-entry.
 *
 * Not called for any other tab: `querySelector` simply finds nothing, and the
 * previous tab's listeners are already gone by then.
 */
function mountProfileCard(body: HTMLElement): void {
  unbindProfileCard?.();
  unbindProfileCard = null;

  const card = body.querySelector<HTMLElement>('[data-profile-card]');
  if (!card) return;

  /**
   * `-1..1` on each axis from the pointer's position in the card's box.
   *
   * Read off `getBoundingClientRect` per move rather than cached: the pane
   * scrolls, and a cached rect makes the tilt drift the moment it does.
   */
  const onMove = (e: PointerEvent): void => {
    const r = card.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const nx = (e.clientX - r.left) / r.width * 2 - 1;
    const ny = (e.clientY - r.top) / r.height * 2 - 1;
    // Y drives the X rotation and vice versa — pushing the cursor DOWN tips
    // the far edge away, which is what "leaning into the cursor" looks like.
    // Sign is inverted on X for that reason. 7 degrees: enough to read as
    // depth on a 104px-tall card, small enough that the portrait's own
    // perspective does not visibly fight the parent's.
    card.style.setProperty('--tilt-x', `${(-ny * 7).toFixed(2)}deg`);
    card.style.setProperty('--tilt-y', `${(nx * 7).toFixed(2)}deg`);
    // The glare tracks the cursor in percentages of the card, so the
    // highlight sits under the pointer rather than at a fixed angle.
    card.style.setProperty('--gx', `${(((nx + 1) / 2) * 100).toFixed(1)}%`);
    card.style.setProperty('--gy', `${(((ny + 1) / 2) * 100).toFixed(1)}%`);
  };

  /**
   * Home on leave, and on pointer CANCEL too.
   *
   * The same discipline `player-controls.ts` documents for its aim state: a
   * gesture interrupted by the OS produces no `pointerleave`, and a card left
   * frozen mid-tilt with the cursor elsewhere looks broken rather than
   * animated.
   */
  const onRest = (): void => {
    card.style.setProperty('--tilt-x', '0deg');
    card.style.setProperty('--tilt-y', '0deg');
    card.style.setProperty('--gx', '50%');
    card.style.setProperty('--gy', '0%');
  };

  card.addEventListener('pointermove', onMove);
  card.addEventListener('pointerleave', onRest);
  card.addEventListener('pointercancel', onRest);
  onRest();

  unbindProfileCard = () => {
    card.removeEventListener('pointermove', onMove);
    card.removeEventListener('pointerleave', onRest);
    card.removeEventListener('pointercancel', onRest);
  };
}

function tabBody(state: AccountState): string {
  switch (state.tab) {
    case 'SOUND': return soundTab();
    case 'CONTROLS': return controlsTab();
    case 'TUTORIAL': return tutorialTab();
    case 'BILLING': return billingTab(state);
    case 'LEDGER': return ledgerTab(state);
    case 'LEGAL': return legalTab();
    default: return profileTab(state);
  }
}

// ─── Profile ────────────────────────────────────────────────────────────────

function profileTab(state: AccountState): string {
  const p = state.profile;
  if (!p) {
    return `<h2 class="tg-set-h2">Profile</h2><div class="tg-set-msgs"></div>
      <p class="tg-set-empty">${state.busy ? 'Reading your file…' : 'Nothing to show.'}</p>`;
  }
  const character = gameStateStore.get().character;
  const level = character?.state === 'ACTIVE' ? character.level : null;
  const name = authStore.get().user?.username ?? p.username ?? p.email;
  const verified = p.emailVerified
    ? '<span class="tg-set-badge tg-set-badge-ok">verified</span>'
    : '<span class="tg-set-badge tg-set-badge-warn">unverified</span>';

  return `
    <h2 class="tg-set-h2">Profile</h2>
    <div class="tg-set-msgs"></div>

    <!-- The survivor card. Structure matters to two other things: the tilt is
         driven by [data-profile-card] (see mountProfileCard) and the preview is
         moved into .tg-set-portrait-slot by mountPreview, so neither hook may
         be renamed without following it. -->
    <div class="tg-set-card" data-profile-card>
      <div class="tg-set-card-glare" aria-hidden="true"></div>
      <div class="tg-set-card-inner">
        <!-- The two photo-corner nubs that used to sit here are gone —
             Janeks' call. The double border carries the framed-print read on
             its own and the triangles were reading as clipped corners. -->
        <div class="tg-set-card-frame">
          <div class="tg-set-portrait-slot"></div>
        </div>
        <div class="tg-set-card-body">
          <p class="tg-set-card-kicker">Settlement Archives</p>
          <p class="tg-set-who-name">${esc(name)}</p>
          <dl class="tg-set-card-facts">
            <div><dt>Joined</dt><dd>${esc(shortDate(p.createdAt))}</dd></div>
            <div><dt>Account</dt><dd>${esc(p.role === 'ADMIN' ? 'Administrator' : 'Survivor')}</dd></div>
          </dl>
        </div>
        ${level === null
          ? '<div class="tg-set-ribbon tg-set-ribbon-none"><span>No<br>survivor</span></div>'
          : `<div class="tg-set-ribbon"><span class="tg-set-ribbon-k">level</span><span class="tg-set-ribbon-n">${level}</span></div>`}
      </div>
    </div>

    <!-- Janeks: "Make the Account section and Preferences section side by
         side (2 columns)". Both drop to ONE field per row inside their column
         — at ~300px a column, two fields across would put a street address in
         140px. -->
    <div class="tg-set-cols">
    <div class="tg-set-section">
      <h3 class="tg-set-h3">Account</h3>
      <div class="tg-set-grid tg-set-grid-1">
        <label class="tg-set-field">
          <span class="tg-set-label">Email ${verified}</span>
          <!-- Read-only: changing an email is a re-verification flow, and core
               owns it. Shown because it is the one identifier that matters. -->
          <input class="tg-set-input" name="email" value="${esc(p.email)}" readonly>
        </label>
        <label class="tg-set-field">
          <span class="tg-set-label">First name</span>
          <input class="tg-set-input" name="firstName" maxlength="${BILLING_LIMITS.name.max}"
            value="${esc(p.firstName ?? '')}" placeholder="First name">
          ${errSlot('firstName')}
        </label>
        <label class="tg-set-field">
          <span class="tg-set-label">Last name</span>
          <input class="tg-set-input" name="lastName" maxlength="${BILLING_LIMITS.name.max}"
            value="${esc(p.lastName ?? '')}" placeholder="Last name">
          ${errSlot('lastName')}
        </label>
      </div>
      <div class="tg-set-actions">
        <button type="button" class="tg-set-btn tg-set-btn-go" data-set-action="save-profile" ${state.busy ? 'disabled' : ''}>Save</button>
      </div>
    </div>

    <div class="tg-set-section">
      <h3 class="tg-set-h3">Preferences</h3>
      <p class="tg-set-hint">Language and currency are saved on their own — they belong to a different part of the server.</p>
      <div class="tg-set-grid tg-set-grid-1">
        <label class="tg-set-field">
          <span class="tg-set-label">Language</span>
          <select class="tg-set-select" name="locale">${localeOptions(state)}</select>
        </label>
        <label class="tg-set-field">
          <span class="tg-set-label">Currency</span>
          <select class="tg-set-select" name="currency">${currencyOptions(state)}</select>
        </label>
      </div>
      <div class="tg-set-actions">
        <button type="button" class="tg-set-btn" data-set-action="save-prefs" ${state.busy ? 'disabled' : ''}>Save preferences</button>
      </div>
    </div>
    </div>

    <!-- One address, core's: the same one a deposit sends to the gateway and
         an invoice prints. Flat and floor go on line 2 (core has no columns
         for them). id="billing-address" is where core's payment picker links. -->
    <div class="tg-set-section tg-set-section-col" id="billing-address">
      <h3 class="tg-set-h3">Billing address</h3>
      ${billingNote(p)}
      <div class="tg-set-grid tg-set-grid-1">
        ${textField('Street and number', 'billingLine1', p.billingLine1, BILLING_LIMITS.line1.max)}
        ${textField('Address line 2 (flat, floor, building)', 'billingLine2', p.billingLine2, BILLING_LIMITS.line2.max)}
        <div class="tg-set-pair">
          ${textField('Post code', 'billingZip', p.billingZip, BILLING_LIMITS.zip.max)}
          ${textField('City', 'billingCity', p.billingCity, BILLING_LIMITS.city.max)}
        </div>
        <label class="tg-set-field">
          <span class="tg-set-label">Country</span>
          <select class="tg-set-select" name="billingCountry">${countryOptions(p.billingCountry)}</select>
          ${errSlot('billingCountry')}
        </label>
        <div data-set-state-field ${stateRequired(p.billingCountry) ? '' : 'hidden'}>
          ${textField('State / province code', 'billingState', p.billingState, BILLING_LIMITS.state.max)}
        </div>
        ${textField('Company (invoices only)', 'billingCompany', p.billingCompany, 160)}
      </div>
      <div class="tg-set-actions">
        <button type="button" class="tg-set-btn tg-set-btn-go" data-set-action="save-profile" ${state.busy ? 'disabled' : ''}>Save</button>
      </div>
    </div>

    <!-- Janeks: "Account password change in columns". Three across, and they
         fit: the pane is ~700px inside its padding, so 3 x ~215px is legible,
         and the three fields belong to ONE action — stacking them made the
         section the tallest thing on the tab for no reason. -->
    <div class="tg-set-section">
      <h3 class="tg-set-h3">Password</h3>
      <p class="tg-set-hint">Changing it signs out every device, this one included.</p>
      <div class="tg-set-grid tg-set-grid-3">
        ${passwordField('Current password', 'currentPassword', 'current-password')}
        ${passwordField('New password', 'newPassword', 'new-password')}
        ${passwordField('New password again', 'repeatPassword', 'new-password')}
      </div>
      <div class="tg-set-actions">
        <button type="button" class="tg-set-btn" data-set-action="change-password" ${state.busy ? 'disabled' : ''}>Change password</button>
      </div>
    </div>

    <div class="tg-set-section tg-set-danger">
      <h3 class="tg-set-h3">Danger zone</h3>
      <p class="tg-set-hint">Deleting the account removes the survivor, the hideout, the house and every item. There is no restore.</p>
      <!-- "Delete account as is" — Janeks. Left alone, save for the one field
           no longer stretching the full pane width. -->
      <div class="tg-set-grid tg-set-grid-1 tg-set-grid-tight">
        ${passwordField('Password', 'deletePassword', 'current-password')}
      </div>
      <div class="tg-set-actions">
        <button type="button" class="tg-set-btn tg-set-btn-danger" data-set-action="delete-account" ${state.busy ? 'disabled' : ''}>Delete my account</button>
      </div>
    </div>
  `;
}

/**
 * `wide` (a `grid-column: 1 / -1` span) is gone with the two-column billing
 * grid it existed for — the address is one column now, so every field is
 * already full-row and the flag had nothing left to mean. `.tg-set-field-wide`
 * survives in the sheet for the Account grid.
 */
function textField(
  label: string,
  name: string,
  value: string | null,
  maxLength: number,
): string {
  return `<label class="tg-set-field">
    <span class="tg-set-label">${esc(label)}</span>
    <input class="tg-set-input" name="${name}" maxlength="${maxLength}" value="${esc(value ?? '')}">
    ${errSlot(name)}
  </label>`;
}

/** Filled in place by `renderBillingErrors` — errors must not rebuild a typed form. */
function errSlot(name: string): string {
  return `<span class="tg-set-field-err" data-set-err="${name}" role="alert" hidden></span>`;
}

const CORE_FIELD_LABEL: Record<string, string> = {
  firstName: 'first name', lastName: 'last name', line1: 'street', line2: 'address line 2',
  city: 'city', zip: 'post code', state: 'state', country: 'country',
};

/**
 * What core would make of the stored address. Card and BLIK take a payment
 * without one; Apple Pay and Open Banking refuse it until it is complete.
 */
function billingNote(p: AccountProfile): string {
  const b = p.billing;
  if (b.complete) return '<p class="tg-set-hint tg-set-billing-ok">Complete — every payment method can use it.</p>';
  const parts = [
    ...b.missing.map((f) => CORE_FIELD_LABEL[f] ?? f),
    ...Object.keys(b.invalid).map((f) => `${CORE_FIELD_LABEL[f] ?? f} (invalid)`),
  ];
  return `<p class="tg-set-hint tg-set-billing-need">Needed for Apple Pay and bank payments (card works without it): ${esc(parts.join(', '))}.</p>`;
}

/** Puts each field's error under its input, without touching what was typed. */
function renderBillingErrors(body: HTMLElement, state: AccountState): void {
  for (const slot of body.querySelectorAll<HTMLElement>('[data-set-err]')) {
    const name = slot.dataset.setErr as ProfileField;
    const message = state.billingErrors[name] ?? '';
    if (slot.textContent !== message) slot.textContent = message;
    slot.hidden = !message;
    const input = slot.parentElement?.querySelector<HTMLElement>('input, select');
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }
}

function passwordField(label: string, name: string, autocomplete: string): string {
  return `<label class="tg-set-field">
    <span class="tg-set-label">${esc(label)}</span>
    <input class="tg-set-input" type="password" name="${name}" autocomplete="${autocomplete}" maxlength="128">
  </label>`;
}

/**
 * The enabled locales, from core's own `/i18n/config`.
 *
 * Never a hardcoded list: `I18nService.isSupported` is what accepts or rejects
 * the write, and a second copy here would offer a language the server refuses.
 * With the config unreachable the dropdown falls back to whatever the account
 * already has, so it can still be read.
 */
function localeOptions(state: AccountState): string {
  const current = state.profile?.locale ?? '';
  const list = state.i18n?.locales ?? (current ? [{ code: current, tag: current, label: current }] : []);
  if (list.length === 0) return '<option value="">—</option>';
  return list.map((l) => `<option value="${esc(l.code)}"${l.code === current ? ' selected' : ''}>${esc(l.label)}</option>`).join('');
}

/**
 * The enabled currencies.
 *
 * The brief says "start with EUR only", and this does not hardcode that: it
 * renders whatever `CurrencyService` reports, which is one entry today. A
 * literal EUR here would have to be found and deleted the day a second
 * currency is enabled, and nothing would point at it.
 */
function currencyOptions(state: AccountState): string {
  const current = state.profile?.currency ?? '';
  const list = state.i18n?.currencies ?? (current ? [{ code: current, symbol: '' }] : []);
  if (list.length === 0) return '<option value="">—</option>';
  return list.map((c) => {
    const label = c.symbol ? `${c.code} (${c.symbol})` : c.code;
    return `<option value="${esc(c.code)}"${c.code === current ? ' selected' : ''}>${esc(label)}</option>`;
  }).join('');
}

function countryOptions(current: string | null): string {
  const blank = `<option value=""${current ? '' : ' selected'}>—</option>`;
  return blank + countries()
    .map((c) => `<option value="${c.code}"${c.code === current ? ' selected' : ''}>${esc(c.name)}</option>`)
    .join('');
}

// ─── Sound ──────────────────────────────────────────────────────────────────

/**
 * Controls.
 *
 * Reads as a reference card first and a settings page second, on purpose: the
 * reported problem was half "I cannot shoot on a laptop" and half "I did not
 * know what the buttons were". The one toggle sits under the scheme it changes.
 */
function controlsTab(): string {
  return `
    <h2 class="tg-set-h2">Controls</h2>
    <div class="tg-set-msgs"></div>
    <div class="tg-set-keys"></div>
  `;
}

function soundTab(): string {
  return `
    <h2 class="tg-set-h2">Sound</h2>
    <div class="tg-set-msgs"></div>
    <div class="tg-set-mix"></div>
  `;
}

/**
 * The control reference plus its one toggle.
 *
 * Repainted from the store rather than written once with the tab, so the
 * toggle's label and pressed state follow the setting even if it is changed
 * from somewhere else.
 */
function renderControls(body: HTMLElement, settings: ControlSettings): void {
  const host = body.querySelector<HTMLElement>('.tg-set-keys');
  if (!host) return;
  const on = settings.fireWithSpace;
  const wanted = `
    <dl class="tg-set-keylist">
      <dt><kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd></dt><dd>Move</dd>
      <dt><kbd>Caps Lock</kbd></dt><dd>Walk or run</dd>
      <dt><kbd>C</kbd></dt><dd>Crouch</dd>
      <dt><kbd>E</kbd></dt><dd>Interact with whatever is in reach</dd>
      <dt>Right mouse (hold)</dt><dd>Raise your weapon and aim where the cursor points</dd>
      <dt>Left mouse</dt><dd>Attack, while aiming. Drag to swing the camera otherwise</dd>
      <dt><kbd>Space</kbd></dt><dd>${on ? 'Attack, while aiming' : '<span class="tg-set-key-off">Off</span>'}</dd>
    </dl>
    <div class="tg-set-opt">
      <button type="button" class="tg-set-toggle${on ? ' tg-set-toggle-on' : ''}"
        data-set-action="fire-space" aria-pressed="${on}">${on ? 'On' : 'Off'}</button>
      <div>
        <p class="tg-set-opt-name">Fire with Space as well as the mouse</p>
        <p class="tg-set-opt-hint">On a trackpad, holding the right button is a two-finger
          press — there is no finger left to click with, so attacking is impossible without
          this. It only adds a key; the left mouse button keeps working either way.</p>
      </div>
    </div>`;
  if (host.innerHTML !== wanted) host.innerHTML = wanted;
}

/**
 * Repainted on every push rather than rebuilt with the tab, because a slider
 * IS the input: dragging it pushes the store, and rewriting the whole tab
 * under a held pointer would drop the drag on the first frame.
 */
function renderMixer(body: HTMLElement, settings: AudioSettings): void {
  const mix = body.querySelector<HTMLElement>('.tg-set-mix');
  if (!mix) return;
  const wanted = AUDIO_CHANNELS.map((c) => {
    const ch = settings[c.id];
    return `
      <div class="tg-set-chan${ch.muted ? ' tg-set-chan-off' : ''}">
        <span class="tg-set-chan-name">${esc(c.label)}</span>
        <input class="tg-set-range" type="range" min="0" max="100" step="1"
          value="${Math.round(ch.level * 100)}" data-set-range="${c.id}"
          aria-label="${esc(c.label)} volume">
        <span class="tg-set-chan-val">${Math.round(ch.level * 100)}%</span>
        <button type="button" class="tg-set-mute${ch.muted ? ' tg-set-mute-on' : ''}"
          data-set-action="mute" data-value="${c.id}"
          aria-pressed="${ch.muted}">${ch.muted ? 'Muted' : 'Mute'}</button>
        <p class="tg-set-chan-hint">${esc(c.hint)}</p>
      </div>`;
  }).join('');
  if (mix.innerHTML === wanted) return;

  // Preserves the drag. Replacing the markup mid-gesture detaches the element
  // the browser is tracking, so the slider would move once and then stick.
  const dragging = document.activeElement as HTMLInputElement | null;
  const draggingId = dragging?.dataset?.setRange ?? null;
  if (draggingId) {
    const live = mix.querySelector<HTMLElement>(`[data-set-range="${draggingId}"]`)
      ?.closest('.tg-set-chan');
    const valueEl = live?.querySelector<HTMLElement>('.tg-set-chan-val');
    if (valueEl) valueEl.textContent = `${Math.round(settings[draggingId as AudioChannelId].level * 100)}%`;
    return;
  }
  mix.innerHTML = wanted;
  for (const range of mix.querySelectorAll<HTMLInputElement>('[data-set-range]')) {
    range.addEventListener('input', () => {
      setChannelLevel(range.dataset.setRange as AudioChannelId, Number(range.value) / 100);
    });
  }
}

// ─── Tutorial ───────────────────────────────────────────────────────────────

/**
 * Janeks: *"The tutorial section in settings - a menu option, inside the view
 * is a horizontal card with text CTA, to go through the tutorial and a primary
 * button."*
 *
 * One card and nothing else. The walkthrough has no settings — no per-step
 * toggles, no "show tips" checkbox — so a tab of switches would be inventing
 * options to fill a page.
 */
function tutorialTab(): string {
  const seen = hasSeenWalkthrough();
  return `
    <h2 class="tg-set-h2">Tutorial</h2>
    <div class="tg-set-msgs"></div>
    <div class="tg-set-cta">
      <div class="tg-set-cta-icon" aria-hidden="true">?</div>
      <div class="tg-set-cta-text">
        <h3 class="tg-set-h3">${seen ? 'Run the walkthrough again' : 'Take the walkthrough'}</h3>
        <p class="tg-set-cta-body">Eight short cards over the top of the game: moving and
        looking, fighting, and where the settlement keeps your things, your work, the road
        out, the arena and everyone else. Takes about two minutes, and you can stop
        part-way.</p>
      </div>
      <button type="button" class="tg-set-btn tg-set-btn-go tg-set-cta-btn"
        data-set-action="start-tutorial">${seen ? 'Start again' : 'Start'}</button>
    </div>
    <div class="tg-set-cta">
      <div class="tg-set-cta-text">
        <h3 class="tg-set-h3">Practice yard</h3>
        <p class="tg-set-cta-body">A safe yard to practise moving, using a knife and a gun,
        searching a crate and wearing gear. It also explains how the map works.
        Nothing there changes your real items.</p>
      </div>
      <button type="button" class="tg-set-btn tg-set-btn-go tg-set-cta-btn"
        data-set-action="start-yard">Go to the yard</button>
    </div>
  `;
}

/**
 * Closes this screen, THEN asks for the tour.
 *
 * In that order and not the other way round: the walkthrough's overlay sits at
 * z-index 1150 and this screen at 1250, so a tour started under an open
 * Settings screen would be invisible — and its first act is to ring the gait
 * indicator, which Settings is covering.
 *
 * `requestWalkthrough` only sets a store field. The overlay that answers lives
 * in `base-view/ui/` and is torn down with the settlement view, which this
 * screen outlives — see that function's docstring for why this direction is
 * the only one available. In a Location there is no settlement UI to point at,
 * so the request waits rather than drawing rings around nothing.
 */
function beginTutorial(): void {
  closeSettingsUI();
  requestWalkthrough();
}

// ─── Invoices ───────────────────────────────────────────────────────────────

/**
 * The invoice table, ALWAYS.
 *
 * Janeks: *"show table section even in case there are no bills — a section
 * that would have invoices table, there were some"*. Before this, an account
 * with no payments got a bare "No invoices yet." line and nothing else, which
 * reads as a tab that failed to load rather than a ledger with nothing in it —
 * there was no heading strip, no columns, nothing to say what WOULD appear
 * here. Same treatment in `ledgerTab`.
 *
 * So the section, its hint and the table head are unconditional, and the three
 * states differ only in the `<tbody>`: rows, one "fetching" row, or one
 * "nothing yet" row. Keeping the shell fixed also means the arrival of the
 * first invoice does not change the page's shape — the placeholder row is
 * replaced in the table that was already there.
 */
function billingTab(state: AccountState): string {
  if (!state.invoices) void loadInvoices();
  const cols = 5;
  const body = !state.invoices
    ? emptyRow(cols, 'Fetching invoices…')
    : state.invoices.items.length === 0
      ? emptyRow(cols, 'No invoices yet. Anything you buy with real money is receipted here.')
      : state.invoices.items.map(invoiceRow).join('');
  const withdrawalsEnabled = getSiteInfo()?.features.withdrawals !== false;

  return `
    <h2 class="tg-set-h2">Invoices</h2>
    <div class="tg-set-msgs"></div>
    ${withdrawalsEnabled ? `
    <div class="tg-set-section">
      <h3 class="tg-set-h3">Withdraw</h3>
      <p class="tg-set-hint">Request a payout to your card or bank account. Processed in EUR.</p>
      <div data-withdraw-mount class="tg-set-withdraw"></div>
    </div>` : ''}
    <div class="tg-set-section">
      <h3 class="tg-set-h3">Payments</h3>
      <p class="tg-set-hint">Every payment, newest first, in the currency it was billed in.</p>
      <div class="tg-set-scroll"><table class="tg-set-table">
        <thead><tr>
          <th>Invoice</th><th>Date</th><th>Method</th><th>Status</th><th class="tg-set-num">Amount</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table></div>
    </div>
  `;
}

/**
 * One full-width row standing in for the rows there aren't.
 *
 * A `colspan` cell rather than a sibling paragraph, so the message sits INSIDE
 * the table it is explaining and inherits its borders and padding — a `<p>`
 * after `</table>` would float under an empty grid and look like two separate
 * failures.
 */
function emptyRow(colspan: number, message: string): string {
  return `<tr class="tg-set-row-empty"><td colspan="${colspan}">${esc(message)}</td></tr>`;
}

function invoiceRow(inv: AccountInvoice): string {
  const url = `${API_BASE}/invoices/${encodeURIComponent(inv.number)}/download`;
  return `<tr>
    <td><a href="${esc(url)}" target="_blank" rel="noopener" class="tg-set-link">${esc(inv.number)}</a></td>
    <td>${esc(shortDate(inv.paidAt ?? inv.createdAt))}</td>
    <td>${esc(inv.paymentMethod ?? '—')}</td>
    <td>${esc(inv.status)}</td>
    <td class="tg-set-num">${esc(money(inv.totalCents, inv.currency))}</td>
  </tr>`;
}

/**
 * Cents to a readable amount IN THE CURRENCY IT WAS BILLED IN.
 *
 * Never converted to the account's preferred currency: an invoice is a record
 * of a payment that happened, and restating it at today's rate would print a
 * number the player was never charged.
 */
function money(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    // An unknown or malformed currency code. Better a plain number with the
    // code beside it than an exception that empties the table.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

// ─── Ledger ─────────────────────────────────────────────────────────────────

/** Same rule as `billingTab` — the table section is unconditional. */
function ledgerTab(state: AccountState): string {
  if (!state.ledger) void loadLedger();
  const cols = 4;
  const rows = !state.ledger
    ? emptyRow(cols, 'Reading the ledger…')
    : state.ledger.items.length === 0
      ? emptyRow(cols, 'Nothing spent or earned yet. Every Cash and Gold movement lands here.')
      : state.ledger.items.map(ledgerRow).join('');
  return `
    <h2 class="tg-set-h2">Ledger</h2>
    <div class="tg-set-msgs"></div>
    <div class="tg-set-section">
      <h3 class="tg-set-h3">Movements</h3>
      <p class="tg-set-hint">Every Cash and Gold movement, newest first. Purchases name what they bought.</p>
      <div class="tg-set-scroll"><table class="tg-set-table">
        <thead><tr>
          <th>Date</th><th>What</th><th class="tg-set-num">Amount</th><th class="tg-set-num">Balance after</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table></div>
      ${state.ledger?.hasMore ? '<p class="tg-set-hint">Showing the most recent 50.</p>' : ''}
    </div>
  `;
}

function ledgerRow(e: AccountLedgerEntry): string {
  // `label` is the server's human rendering; `description` is the raw slug it
  // was resolved from. The label can miss (a deleted ShopItem, a renamed
  // vendor), and printing the slug is better than printing nothing.
  const what = e.itemName
    ? `${esc(e.itemName)}${e.quantity && e.quantity > 1 ? ` ×${e.quantity}` : ''}`
    : esc(e.label || e.description);
  const premium = e.currency === 'PREMIUM';
  const unit = premium ? 'Gold' : 'Cash';
  const coin = premium ? goldIconHtml('tg-set-coin') : cashCoinHtml('sm');
  const sign = e.amount < 0 ? '−' : '+';
  return `<tr>
    <td>${esc(shortDate(e.createdAt))}</td>
    <td>${what}</td>
    <td class="tg-set-num ${e.amount < 0 ? 'tg-set-debit' : 'tg-set-credit'}">${sign}${coin}${Math.abs(e.amount)}<span class="tg-set-unit"> ${unit}</span></td>
    <td class="tg-set-num ${e.currency === 'PREMIUM' ? 'tg-set-gold' : 'tg-set-cash'}">${coin}${e.balanceAfter}</td>
  </tr>`;
}

// ─── Legal ──────────────────────────────────────────────────────────────────

function legalTab(): string {
  // Real links in a new tab: the settings screen keeps its place, and the
  // document is a page of its own (`/terms-and-conditions` and friends).
  return `<h2 class="tg-set-h2">Legal</h2><div class="tg-set-msgs"></div>
    <div class="tg-set-docs">${LEGAL_DOCUMENTS.map((d) => `
      <a class="tg-set-doc" href="${esc(legalPath(d.slug))}" target="_blank" rel="noopener">
        <span>${esc(d.label)}</span><span class="tg-set-doc-arrow" aria-hidden="true">↗</span>
      </a>`).join('')}</div>`;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * A date the player can read, in THEIR locale.
 *
 * Returns the raw string on anything unparseable rather than "Invalid Date" —
 * a server that changes a date format should not blank a whole column.
 */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}
